import { Link } from 'react-router-dom';

export const Help = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
    {/* Navigation */}
    <nav className="relative z-10 flex items-center justify-between p-6 lg:px-8">
      <div className="flex items-center">
        <Link to="/" className="flex items-center">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <span className="ml-3 text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Finderz AI
          </span>
        </Link>
      </div>
      <div className="flex items-center gap-4">
        <Link 
          to="/login" 
          className="text-[#444] hover:text-[#171717] transition duration-300 font-medium"
        >
          Sign In
        </Link>
        <Link 
          to="/register" 
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transform hover:scale-105 transition duration-300 font-medium"
        >
          Get Started
        </Link>
      </div>
    </nav>

    {/* Help Content */}
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-8 lg:p-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#171717] mb-4">
            Help Center
          </h1>
          <p className="text-lg text-[#555]">
            Find answers to common questions and learn how to get the most out of Finderz AI
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#171717] mb-4">Getting Started</h2>
            <div className="space-y-4">
              <div className="bg-[#f5f5f5] p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">How do I create my first project?</h3>
                <p className="text-[#444]">
                  After signing in, click the "New Project" button in your dashboard. Fill in the project details, add team members, and start creating tasks and phases.
                </p>
              </div>
              <div className="bg-[#f5f5f5] p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">How do I invite team members?</h3>
                <p className="text-[#444]">
                  Go to your project settings, click "Team" tab, and use the "Invite Members" button. Enter their email addresses and select their roles.
                </p>
              </div>
              <div className="bg-[#f5f5f5] p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">How do I track project progress?</h3>
                <p className="text-[#444]">
                  Use the Project Plan view to see all tasks, their status, and deadlines. The dashboard provides real-time analytics and progress reports.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#171717] mb-4">Project Management</h2>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">What are phases and how do I use them?</h3>
                <p className="text-[#444]">
                  Phases help organize your project into logical stages. Create phases like "Planning", "Development", "Testing", and assign tasks to each phase for better organization.
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">How do I set up notifications?</h3>
                <p className="text-[#444]">
                  Go to your profile settings and configure notification preferences. You can choose to receive updates via email, in-app notifications, or both.
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">Can I attach documents to tasks?</h3>
                <p className="text-[#444]">
                  Yes! Click on any task and use the "Attach Document" button. You can upload files, images, and other resources directly to tasks.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#171717] mb-4">Troubleshooting</h2>
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">I can't see my project data</h3>
                <p className="text-[#444]">
                  Try refreshing the page or clearing your browser cache. If the issue persists, check your internet connection or contact support.
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">Why can't I edit a task?</h3>
                <p className="text-[#444]">
                  You may not have the required permissions. Check with your project manager or admin to ensure you have the right access level.
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">How do I reset my password?</h3>
                <p className="text-[#444]">
                  Click "Forgot Password" on the login page. Enter your email address and follow the instructions in the reset email.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#171717] mb-4">Advanced Features</h2>
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">How do I use the API?</h3>
                <p className="text-[#444]">
                  Visit our API documentation at api.finderz.ai for detailed guides, endpoints, and authentication methods.
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">Can I integrate with other tools?</h3>
                <p className="text-[#444]">
                  Yes! We support integrations with Slack, Microsoft Teams, GitHub, and many other popular tools. Check the integrations page in your settings.
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#171717] mb-2">How do I export my data?</h3>
                <p className="text-[#444]">
                  Go to project settings and click "Export Data". You can download your project data in CSV, JSON, or PDF formats.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#171717] mb-4">Still Need Help?</h2>
            <p className="text-[#444] leading-relaxed mb-4">
              Can't find what you're looking for? Our support team is here to help!
            </p>
            <div className="bg-[#f5f5f5] p-6 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-[#171717] mb-2">Contact Support</h3>
                  <p className="text-[#444] text-sm mb-2">
                    <strong>Email:</strong> support@finderz.ai<br />
                    <strong>Response time:</strong> Within 2 hours
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-[#171717] mb-2">Live Chat</h3>
                  <p className="text-[#444] text-sm mb-2">
                    <strong>Available:</strong> Mon-Fri, 9 AM - 6 PM PST<br />
                    <strong>Response time:</strong> Immediate
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link 
            to="/" 
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transform hover:scale-105 transition duration-300 font-medium"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>

    {/* Footer */}
    <footer className="bg-white/80 backdrop-blur-sm mt-12">
      <div className="mx-auto max-w-7xl px-6 py-8 md:flex md:items-center md:justify-between lg:px-8">
        <div className="mt-4 md:order-1 md:mt-0">
          <p className="text-center text-xs leading-5 text-[#888]">
            &copy; 2025 Finderz AI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  </div>
); 